/*
 * PendingReviewBanner — top-of-page alert for admins.
 * Shows counts of drivers awaiting VA credentialing + pending TP applications.
 * Polls /api/tpt/queue/pending every 30s.
 */
import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";

export default function PendingReviewBanner() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/tpt/queue/pending");
      setData(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (!data) return null;
  const total = (data.pending_drivers || 0) + (data.pending_tp_applications || 0);
  if (total === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center gap-3" data-testid="pending-review-banner">
      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <span className="font-semibold">Action needed:</span>{" "}
        {data.pending_drivers > 0 && (
          <span data-testid="pending-drivers-count">
            <b>{data.pending_drivers}</b> driver{data.pending_drivers !== 1 ? "s" : ""} awaiting credentialing
          </span>
        )}
        {data.pending_drivers > 0 && data.pending_tp_applications > 0 && " · "}
        {data.pending_tp_applications > 0 && (
          <span data-testid="pending-tp-count">
            <b>{data.pending_tp_applications}</b> TP application{data.pending_tp_applications !== 1 ? "s" : ""} pending
          </span>
        )}
      </div>
      {data.pending_drivers > 0 && (
        <button
          onClick={() => nav("/admin/credentialing")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:text-amber-700 underline"
          data-testid="review-drivers-btn"
        >
          Review drivers <ExternalLink size={12} />
        </button>
      )}
      {data.pending_tp_applications > 0 && (
        <button
          onClick={() => nav("/admin/providers")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:text-amber-700 underline"
          data-testid="review-apps-btn"
        >
          Review applications <ExternalLink size={12} />
        </button>
      )}
    </div>
  );
}
